import type { AppContext } from "./context";
import type { Take } from "../core/types";
import { el, formatWhen, on, showBanner, toast } from "./dom";
import { formatBytes, formatDuration, listTakes, deleteTake, takeBlob } from "../store/takes";
import { folderPermission, getSaveFolder } from "../store/destination";
import { openEditor } from "./editor";

export function mountLibraryUi(ctx: AppContext): { refresh: () => Promise<void> } {
  const grid = el<HTMLDivElement>("library-grid");
  const empty = el<HTMLDivElement>("library-empty");
  const folderButton = el<HTMLButtonElement>("open-folder");
  const refreshButton = el<HTMLButtonElement>("refresh-library");

  async function refresh(): Promise<void> {
    const takes = await listTakes();
    grid.replaceChildren();
    empty.hidden = takes.length > 0;
    for (const take of takes) grid.append(card(take));
  }

  function card(take: Take): HTMLElement {
    const node = document.createElement("article");
    node.className = "card";

    const thumb = document.createElement("button");
    thumb.type = "button";
    thumb.className = "card-thumb";
    thumb.title = `Play ${take.name}`;
    if (take.thumb) thumb.style.backgroundImage = `url(${take.thumb})`;
    const play = document.createElement("span");
    play.className = "play";
    play.textContent = "▶";
    thumb.append(play);
    on(thumb, "click", () => void play_(take));

    const body = document.createElement("div");
    body.className = "card-body";
    const name = document.createElement("div");
    name.className = "card-name";
    name.textContent = take.name;
    name.title = take.fileName;
    const sub = document.createElement("div");
    sub.className = "card-sub";
    const parts = [
      take.recovered ? "recovered" : formatDuration(take.durationMs),
      formatBytes(take.bytes),
      formatWhen(take.createdAt),
    ];
    sub.textContent = parts.join(" · ");

    const actions = document.createElement("div");
    actions.className = "card-actions";
    actions.append(
      actionButton("Edit", () => void openEditor(ctx, take)),
      actionButton("Save a copy", () => void download(take)),
      actionButton("Remove", () => void remove(take), "danger"),
    );

    body.append(name, sub, actions);
    node.append(thumb, body);
    return node;
  }

  function actionButton(label: string, run: () => void, variant?: string): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `btn small${variant ? ` ${variant}` : ""}`;
    button.textContent = label;
    on(button, "click", run);
    return button;
  }

  async function play_(take: Take): Promise<void> {
    try {
      const blob = await takeBlob(take);
      const url = URL.createObjectURL(blob);
      const dialog = document.createElement("dialog");
      dialog.className = "dialog";
      dialog.style.maxWidth = "min(920px, 92vw)";
      const video = document.createElement("video");
      video.src = url;
      video.controls = true;
      video.autoplay = true;
      video.style.width = "100%";
      video.style.borderRadius = "10px";
      const close = document.createElement("div");
      close.className = "dialog-actions";
      close.append(
        actionButton("Close", () => dialog.close()),
      );
      dialog.append(video, close);
      dialog.addEventListener("close", () => {
        URL.revokeObjectURL(url);
        dialog.remove();
      });
      document.body.append(dialog);
      dialog.showModal();
    } catch (error) {
      toast(
        "The file needs permission again — click “Folder access” and allow it once.",
        "error",
      );
      console.error(error);
    }
  }

  async function download(take: Take): Promise<void> {
    try {
      const blob = await takeBlob(take);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = take.fileName;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (error) {
      toast("Could not read the file — re-allow folder access.", "error");
      console.error(error);
    }
  }

  async function remove(take: Take): Promise<void> {
    // Delete the file through the handle the take was written with. The
    // current save folder may be a different one entirely.
    let fileGone = true;
    try {
      // remove() is newer than the DOM typings; Chrome has had it since 110.
      const removable = take.fileHandle as (FileSystemFileHandle & { remove?: () => Promise<void> }) | null;
      if (removable?.remove) await removable.remove();
      else if (take.fileHandle) throw new Error("this browser cannot remove files");
      else {
        const folder = await getSaveFolder();
        if (folder && (await folderPermission(folder)) === "granted") {
          await folder.handle.removeEntry(take.fileName);
        }
      }
    } catch (error) {
      fileGone = false;
      console.error("file remove failed", error);
    }
    await deleteTake(take.id);
    await refresh();
    toast(fileGone ? "Removed." : "Removed from the list — the file is still on disk.", fileGone ? "info" : "error");
  }

  on(refreshButton, "click", () => void refresh());
  on(folderButton, "click", async () => {
    const folder = await getSaveFolder();
    if (!folder) {
      showBanner("No folder yet — choose one in Settings.", {
        label: "Open Settings",
        run: () => ctx.showTab("settings"),
      });
      return;
    }
    const state = await folderPermission(folder);
    if (state === "granted") {
      toast(`Folder access is granted for “${folder.name}”.`, "ok");
    } else {
      showBanner(`“${folder.name}” needs permission again.`, {
        label: "Allow",
        run: () => void refresh(),
      });
    }
  });

  return { refresh };
}
